import type { IntlShape } from 'react-intl';

import { defineMessages } from 'react-intl';

import type { IconType } from '~/eds/src/types/IconType';

export type UnifiedFieldBootstrapStatus =
  | 'queued'
  | 'running'
  | 'retrying'
  | 'succeeded'
  | 'failed';

type ChipStatus = 'info' | 'success' | 'danger' | 'inactive';

type SetupDisplay = 'inProgress' | 'ready' | 'failed' | 'notApplicable';

export const unifiedFieldBootstrapColumnMessages = defineMessages({
  columnTitle: {
    id: 'AdminPage.unifiedFieldBootstrapColumnTitle',
    defaultMessage: 'Unified Fields',
    description:
      'Column title for the client-creation Unified Fields setup job status',
  },
  columnInfo: {
    id: 'AdminPage.unifiedFieldBootstrapColumnInfo',
    defaultMessage:
      'Status of the Unified Fields setup job started when this client was created.',
    description:
      'Tooltip for the Unified Fields setup status column. Not Applicable means no client-creation job exists.',
  },
});

const statusMessages = defineMessages({
  inProgress: {
    id: 'AdminPage.unifiedFieldBootstrapStatus.inProgress',
    defaultMessage: 'In Progress',
    description: 'Unified Fields setup is queued, running, or retrying',
  },
  ready: {
    id: 'AdminPage.unifiedFieldBootstrapStatus.ready',
    defaultMessage: 'Ready',
    description: 'Unified Fields setup completed successfully',
  },
  failed: {
    id: 'AdminPage.unifiedFieldBootstrapStatus.failed',
    defaultMessage: 'Failed',
    description: 'Unified Fields setup failed',
  },
  notApplicable: {
    id: 'AdminPage.unifiedFieldBootstrapStatus.notApplicable',
    defaultMessage: 'Not Applicable',
    description:
      'Unified Fields setup status when the client has no create-client job',
  },
  inProgressTooltip: {
    id: 'AdminPage.unifiedFieldBootstrapStatus.inProgressTooltip',
    defaultMessage: 'Unified Fields setup has not finished.',
    description:
      'Tooltip for the In Progress Unified Fields status. Covers queued, running, and retrying jobs.',
  },
  readyTooltip: {
    id: 'AdminPage.unifiedFieldBootstrapStatus.readyTooltip',
    defaultMessage: 'Unified Fields setup finished successfully.',
    description: 'Tooltip for the Ready Unified Fields status',
  },
  failedTooltip: {
    id: 'AdminPage.unifiedFieldBootstrapStatus.failedTooltip',
    defaultMessage: 'Unified Fields setup did not finish successfully.',
    description: 'Tooltip for the Failed Unified Fields status',
  },
  notApplicableTooltip: {
    id: 'AdminPage.unifiedFieldBootstrapStatus.notApplicableTooltip',
    defaultMessage: 'This client has no Unified Fields setup job.',
    description: 'Tooltip for the Not Applicable Unified Fields status',
  },
});

const DISPLAY_BY_JOB_STATUS = {
  queued: 'inProgress',
  running: 'inProgress',
  retrying: 'inProgress',
  succeeded: 'ready',
  failed: 'failed',
} as const satisfies Record<UnifiedFieldBootstrapStatus, SetupDisplay>;

const CHIP_BY_DISPLAY = {
  inProgress: {
    icon: 'loading',
    status: 'info',
  },
  ready: {
    icon: 'status-success',
    status: 'success',
  },
  failed: {
    icon: 'status-danger',
    status: 'danger',
  },
  notApplicable: {
    icon: 'block',
    status: 'inactive',
  },
} as const satisfies Record<
  SetupDisplay,
  { icon: IconType; status: ChipStatus }
>;

const SORT_RANK = {
  succeeded: 0,
  queued: 1,
  running: 1,
  retrying: 1,
  failed: 2,
} as const satisfies Record<UnifiedFieldBootstrapStatus, number>;

const NOT_APPLICABLE_SORT_RANK = 3;

type BootstrapPayload = {
  status?: string | null;
  completed?: boolean;
} | null;

type BootstrapClient = {
  unifiedFieldBootstrapStatus?: string | null;
  unifiedFieldBootstrap?: BootstrapPayload;
  unified_field_bootstrap?: BootstrapPayload;
};

export function readUnifiedFieldBootstrapStatus(
  client: BootstrapClient | null | undefined,
) {
  if (!client) return null;
  const direct = client.unifiedFieldBootstrapStatus;
  if (typeof direct === 'string' && isUnifiedFieldBootstrapStatus(direct)) {
    return direct;
  }
  const bootstrap =
    client.unified_field_bootstrap ?? client.unifiedFieldBootstrap;
  if (!bootstrap || typeof bootstrap !== 'object') return null;
  if (
    typeof bootstrap.status === 'string' &&
    isUnifiedFieldBootstrapStatus(bootstrap.status)
  ) {
    return bootstrap.status;
  }
  if (bootstrap.completed === true) return 'succeeded';
  return null;
}

const isUnifiedFieldBootstrapStatus = (
  status: string,
): status is UnifiedFieldBootstrapStatus => status in DISPLAY_BY_JOB_STATUS;

export function compareUnifiedFieldBootstrapStatus(
  left: BootstrapClient,
  right: BootstrapClient,
) {
  return (
    bootstrapStatusSortRank(readUnifiedFieldBootstrapStatus(left)) -
    bootstrapStatusSortRank(readUnifiedFieldBootstrapStatus(right))
  );
}

function bootstrapStatusSortRank(status: string | null) {
  if (!status || !isUnifiedFieldBootstrapStatus(status)) {
    return NOT_APPLICABLE_SORT_RANK;
  }
  return SORT_RANK[status];
}

const TOOLTIP_BY_DISPLAY = {
  inProgress: statusMessages.inProgressTooltip,
  ready: statusMessages.readyTooltip,
  failed: statusMessages.failedTooltip,
  notApplicable: statusMessages.notApplicableTooltip,
} as const;

function chipFor(intl: IntlShape, display: SetupDisplay) {
  const chip = CHIP_BY_DISPLAY[display];
  return [
    {
      text: intl.formatMessage(statusMessages[display]),
      icon: chip.icon,
      status: chip.status,
      tooltip: intl.formatMessage(TOOLTIP_BY_DISPLAY[display]),
    },
  ];
}

export function getUnifiedFieldBootstrapChips(
  intl: IntlShape,
  status: string | null | undefined,
) {
  if (!status) return chipFor(intl, 'notApplicable');
  if (!isUnifiedFieldBootstrapStatus(status)) {
    return [{ text: status, status: 'inactive' as const }];
  }
  return chipFor(intl, DISPLAY_BY_JOB_STATUS[status]);
}
